/**
 * NotificationSettingsPage - the single web notification surface (#2652)
 *
 * Route: /settings/notifications (ProtectedRoute). /me/notifications now
 * redirects here (App.tsx). Rebuilt as an Email / SMS / Push channel matrix:
 * rows = notification types, columns = channels, plus per-channel master
 * toggles. The grid is derived from the pref keys the API advertises
 * (lib/notificationMatrix.ts), never a hardcoded list, so the per-type Push
 * cells (and the push-only chat_messages row) appear automatically the moment
 * the backend half (#2617 / PR #2654) returns push_* per-type keys.
 *
 * Channel pattern (#1630): channels with real gates (SMS carrier consent + a
 * verified phone; push OS permission) have a master toggle; email has none.
 * Rule 22: save/reset is inline at the end of the form, never fixed-bottom.
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Bell, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { TopBar, Button, Skeleton } from '../../components';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useSendTestNotification,
  type UpdateNotificationPreferencesRequest,
} from '../../hooks/useNotifications';
import { useAuth } from '../../hooks/useAuth';
import {
  buildNotificationMatrix,
  bucketMeta,
  cellKey,
  type NotificationChannel,
} from '../../lib/notificationMatrix';

const CHANNEL_META: Record<
  NotificationChannel,
  { label: string; Icon: typeof Mail }
> = {
  email: { label: 'Email', Icon: Mail },
  sms: { label: 'SMS', Icon: MessageSquare },
  push: { label: 'Push', Icon: Smartphone },
};

// #578 / #3010 A2/A3: the weekly-digest pipeline is dormant — sendWeeklyDigestEmail
// has zero call sites and no cron invokes it, so the email_digest / sms_digest cells
// are toggles that change nothing (Rule 33 phantom control); sms_digest is also
// unmappable by construction (SmsTemplatePrefKey excludes it, #1603). The mobile
// surface already hides these rows behind the same decision (#3066); this is the web
// half. The backend preference columns are left intact — this gates the UI only. Flip
// to true in the same change that wires the digest producer under #578.
const SHOW_WEEKLY_DIGEST_TOGGLES = false;

// The bucket id (from NOTIFICATION_BUCKET_META / the advertised email_digest +
// sms_digest keys) whose row is hidden until the digest ships.
const DIGEST_BUCKET = 'digest';

// Shared grid template so the column-header row and every type row line up on
// wider viewports. On narrow viewports every row collapses to a single column
// (grid-cols-1) and each cell self-labels its channel - no horizontal scroll.
const ROW_GRID =
  'grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_5.5rem] gap-x-3 gap-y-2 sm:items-center';

interface MatrixSwitchProps {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onToggle: () => void;
}

/**
 * Compact role=switch cell. Own control (not the labelled shared Toggle) so the
 * accessible name can carry both the column (channel) and row (type).
 *
 * The 44x24 visual track sits inside a >=44x44 button so the whole hit area is
 * tappable (touch-target minimum). The track COLOUR and thumb POSITION both
 * reflect `checked` alone - a disabled-but-checked switch must still read as ON
 * (visual and aria-checked must agree); `disabled` only dims it.
 */
function MatrixSwitch({ checked, disabled = false, ariaLabel, onToggle }: MatrixSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onToggle();
      }}
      className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg oro-transition focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-oro-bg ${
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      }`}
    >
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full oro-transition ${
          checked ? 'bg-accent shadow-glow' : 'bg-oro-surface-alt border border-oro-border'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full oro-transition ${
            checked ? 'translate-x-6 bg-black' : 'translate-x-1 bg-text-secondary'
          }`}
        />
      </span>
    </button>
  );
}

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useNotificationPreferences();
  const updateMutation = useUpdateNotificationPreferences();
  const testMutation = useSendTestNotification();

  const preferences = data?.preferences;
  const hasVerifiedPhone = !!user?.phone_verified;

  // Staged local edits keyed by pref key. A key is only a real change if it
  // differs from the server value, so Save PATCHes the diff and nothing else.
  const [staged, setStaged] = useState<Record<string, boolean>>({});

  const valueFor = (key: string): boolean =>
    key in staged ? staged[key] : Boolean(preferences?.[key as keyof typeof preferences]);

  const changedKeys = preferences
    ? Object.keys(staged).filter(
        (k) => staged[k] !== Boolean(preferences[k as keyof typeof preferences]),
      )
    : [];
  const hasChanges = changedKeys.length > 0;

  const stageCell = (channel: NotificationChannel, bucket: string) => {
    const key = cellKey(channel, bucket);
    const next = !valueFor(key);
    setStaged((prev) => ({ ...prev, [key]: next }));
  };

  // SMS master is the carrier-consent gate (#1630 / TCR filing CACFI2E): turning
  // it off cascades every per-type SMS flag off for a clean opt-out, turning it
  // on re-arms them all (surfaced in the copy). Push has no such requirement, so
  // its master is a plain toggle and the per-type push cells disable while off.
  const stageSmsMaster = (enabled: boolean, buckets: string[], presentKeys: Set<string>) => {
    setStaged((prev) => {
      const next: Record<string, boolean> = { ...prev, sms_enabled: enabled };
      for (const bucket of buckets) {
        const key = cellKey('sms', bucket);
        if (presentKeys.has(key)) next[key] = enabled;
      }
      return next;
    });
  };

  const stagePushMaster = (enabled: boolean) => {
    setStaged((prev) => ({ ...prev, push_enabled: enabled }));
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    // Snapshot exactly the values being saved. Cells stay interactive during the
    // save (no freeze), so a toggle flipped while the PATCH is in flight must NOT
    // be wiped by the success clear (George save-race). We clear ONLY the keys we
    // sent, and only if their staged value is still the one we saved - a late
    // re-toggle of a saved key to a new value survives too.
    const saved: Record<string, boolean> = {};
    for (const k of changedKeys) saved[k] = staged[k];
    // Annotation instead of an `as` assertion (#2657). `as` fully suppresses
    // the compiler; an annotation keeps the payload COUPLED to the request type,
    // so the build breaks if `updatePreferences`'s parameter shape (or the RHS
    // value type) ever drifts to something not assignable. That is the guarantee
    // this line buys - and the ceiling of it.
    //
    // What it does NOT buy: proof that every key is a member of NotificationPrefKey.
    // `saved` is a `Record<string, boolean>`, which is structurally assignable to
    // UpdateNotificationPreferencesRequest (= Partial<Record<NotificationPrefKey, boolean>>)
    // regardless of whether the FE union is synced to the backend - the target's
    // keys are all optional and the source's string index signature satisfies them.
    // So compile-time key membership is intentionally impossible here, by design:
    // staging is string-keyed by construction (`cellKey`/`masterKeyFor` derive keys
    // from whatever channels and buckets the API advertises, including any future
    // bucket not yet in the union), those keys flow through at runtime via the spread
    // (an annotation strips nothing), and the backend re-validates every key against
    // its own Zod schema. Backend Zod is the real key gate, not this annotation.
    const payload: UpdateNotificationPreferencesRequest = { ...saved };
    try {
      await updateMutation.mutateAsync(payload);
      setStaged((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(saved)) {
          if (next[k] === saved[k]) delete next[k];
        }
        return next;
      });
    } catch {
      // Error surfaced via useOroMutation + Sonner; staged edits are retained.
    }
  };

  const handleReset = () => setStaged({});

  const handleSendTest = async () => {
    try {
      await testMutation.mutateAsync();
    } catch {
      // Error toast handled by useOroMutation.
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Notifications" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-6">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Notifications" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4">
            <p className="text-center text-text-secondary">
              Unable to load notification preferences
            </p>
          </div>
        </div>
      </div>
    );
  }

  const matrix = buildNotificationMatrix(preferences);
  // Hide the dormant Weekly Digest row until #578 ships (see SHOW_WEEKLY_DIGEST_TOGGLES).
  // One filter drops both the email_digest and sms_digest cells; matrix.presentKeys and
  // the channel columns stay intact, so re-enabling is a single boolean flip.
  const visibleBuckets = SHOW_WEEKLY_DIGEST_TOGGLES
    ? matrix.buckets
    : matrix.buckets.filter((bucket) => bucket !== DIGEST_BUCKET);
  const smsEnabled = valueFor('sms_enabled');
  const pushEnabled = valueFor('push_enabled');

  const cellDisabled = (channel: NotificationChannel): boolean => {
    if (channel === 'sms') return !hasVerifiedPhone || !smsEnabled;
    if (channel === 'push') return !pushEnabled;
    return false; // email has no channel gate
  };

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Notifications" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-oro-surface-alt rounded-full flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5 text-accent" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-text-primary">Notifications</h1>
              <p className="text-sm text-text-secondary">Manage how ORO move™ keeps you informed</p>
            </div>
          </div>

          {/* Channel controls: per-channel master toggles + deliverability. */}
          <section className="bg-oro-surface border border-oro-border rounded-xl divide-y divide-oro-border">
            {matrix.channels.includes('email') && (
              <div className="flex items-start gap-3 p-4">
                <Mail className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h2 className="font-medium text-text-primary">Email</h2>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Always on for the types you choose below.
                  </p>
                </div>
              </div>
            )}

            {matrix.channels.includes('sms') && (
              <div className="flex items-start gap-3 p-4">
                <MessageSquare className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h2 className="font-medium text-text-primary">SMS</h2>
                  {hasVerifiedPhone ? (
                    <p className="text-sm text-text-secondary mt-0.5">
                      Text alerts to your verified phone. Enabling turns on every SMS type; you can
                      then switch off the ones you do not want.
                    </p>
                  ) : (
                    // No verified phone: keep the SMS master + per-type cells VISIBLE but
                    // disabled (endowment - the control is theirs once a phone is added),
                    // with a single inline link to where phone add/verify lives
                    // (ProfileSettingsPage PhoneSection, #1461).
                    <div className="text-sm text-text-secondary mt-0.5">
                      <p>SMS is off until you add a verified phone.</p>
                      <Link
                        to="/settings/profile"
                        className="mt-0.5 inline-flex items-center gap-1 text-accent hover:underline"
                      >
                        <Smartphone className="w-4 h-4" aria-hidden="true" />
                        Add a verified phone to enable SMS
                      </Link>
                    </div>
                  )}
                </div>
                <MatrixSwitch
                  checked={smsEnabled}
                  disabled={!hasVerifiedPhone}
                  ariaLabel="Enable SMS notifications"
                  // Cascade over ALL buckets (matrix.buckets, not visibleBuckets):
                  // the SMS master is the carrier-consent opt-out (#1630), so it
                  // must still zero the intact sms_digest column even though its
                  // row is hidden — a clean opt-out leaves no SMS column set.
                  onToggle={() => stageSmsMaster(!smsEnabled, matrix.buckets, matrix.presentKeys)}
                />
              </div>
            )}

            {matrix.channels.includes('push') && (
              <div className="flex items-start gap-3 p-4">
                <Smartphone className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h2 className="font-medium text-text-primary">Push</h2>
                  <p className="text-sm text-text-secondary mt-0.5">
                    Delivered to the ORO move app on your mobile devices. Allow notifications when
                    your device prompts you.
                  </p>
                </div>
                <MatrixSwitch
                  checked={pushEnabled}
                  ariaLabel="Enable push notifications"
                  onToggle={() => stagePushMaster(!pushEnabled)}
                />
              </div>
            )}
          </section>

          {/* The matrix: rows = notification types, columns = channels. */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-5 h-5 text-accent" />
              <h2 className="text-lg font-semibold text-text-primary">Notification types</h2>
            </div>

            <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
              {/* Column header row - desktop only; on mobile each cell self-labels. */}
              <div className={`${ROW_GRID} hidden sm:grid pb-2 mb-2 border-b border-oro-border`}>
                <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                  Type
                </span>
                {matrix.channels.map((channel) => {
                  const { label, Icon } = CHANNEL_META[channel];
                  return (
                    <div
                      key={channel}
                      className="flex items-center justify-center gap-1.5 text-xs font-medium uppercase tracking-wide text-text-muted"
                    >
                      <Icon className="w-4 h-4" />
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>

              <ul className="divide-y divide-oro-border">
                {visibleBuckets.map((bucket) => {
                  const meta = bucketMeta(bucket);
                  return (
                    <li key={bucket} className={`${ROW_GRID} py-3`}>
                      <div className="min-w-0">
                        <p className="font-medium text-text-primary">{meta.label}</p>
                        {meta.description && (
                          <p className="text-sm text-text-secondary mt-0.5">{meta.description}</p>
                        )}
                      </div>

                      {matrix.channels.map((channel) => {
                        const key = cellKey(channel, bucket);
                        const exists = matrix.presentKeys.has(key);
                        const { label } = CHANNEL_META[channel];
                        return (
                          <div
                            key={channel}
                            className="flex items-center justify-between sm:justify-center gap-2"
                          >
                            <span className="sm:hidden text-xs font-medium uppercase tracking-wide text-text-muted">
                              {label}
                            </span>
                            {exists ? (
                              <MatrixSwitch
                                checked={valueFor(key)}
                                disabled={cellDisabled(channel)}
                                ariaLabel={`${label}: ${meta.label}`}
                                onToggle={() => stageCell(channel, bucket)}
                              />
                            ) : (
                              <span
                                className="text-text-muted text-sm select-none"
                                title={`${label} not available for ${meta.label}`}
                              >
                                &mdash;
                                <span className="sr-only">
                                  {label} not available for {meta.label}
                                </span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>

          {/* SMS consent disclosure - always visible (10DLC / #1660). */}
          <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3 text-[11px] leading-relaxed text-text-muted space-y-2">
            <p>
              SMS notifications are sent to your verified phone number. Message frequency depends on
              your event activity. Message and data rates may apply.
            </p>
            <p>
              Reply <strong className="text-text-secondary">STOP</strong> to opt out at any time. For
              help, visit{' '}
              <Link to="/help/sms" className="underline hover:text-text-secondary">
                oro.fit/help/sms
              </Link>
              .
            </p>
          </div>

          {/* Test notification */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-oro-surface-alt rounded-full flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-text-secondary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-text-primary">Test Your Notifications</h3>
                <p className="text-xs text-text-muted mt-1">
                  Send a test email to verify your notification settings are working correctly.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleSendTest}
                  disabled={testMutation.isPending}
                  loading={testMutation.isPending}
                  className="mt-3"
                >
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>

          {/* Help note */}
          <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4">
            <p className="text-sm text-text-secondary">
              You can change these preferences anytime. Critical notifications like security alerts
              are always sent regardless of your settings.
            </p>
          </div>

          {/* Save / Reset - inline at the end of the form (Rule 22: never fixed-bottom). */}
          {hasChanges && (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleReset}
                disabled={updateMutation.isPending}
                className="flex-1"
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="flex-1"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
